
import { useEffect, useRef } from 'react';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          section.classList.add('hero-animated');
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="home" ref={sectionRef} className="relative min-h-screen w-full overflow-hidden">
      <style>{`
        @keyframes kenBurnsLoop {
          0%   { transform: scale(1); }
          50%  { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes heroNameReveal {
          0%   { opacity: 0; transform: translateY(40px); letter-spacing: 0.12em; }
          60%  { opacity: 1; transform: translateY(-4px); }
          100% { opacity: 1; transform: translateY(0); letter-spacing: inherit; }
        }
        @keyframes heroSubtitleSlide {
          from { opacity: 0; transform: translateX(-18px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes heroQuoteFade {
          0%   { opacity: 0; transform: translateY(20px) skewX(-1deg); }
          100% { opacity: 1; transform: translateY(0) skewX(0deg); }
        }
        @keyframes heroLineExpand {
          from { opacity: 0; transform: scaleX(0); }
          to   { opacity: 1; transform: scaleX(1); }
        }
        @keyframes heroDescFade {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes heroPortraitIn {
          from { opacity: 0; transform: translateX(48px) scale(0.97); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes heroButtonFade {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes portraitGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(212,137,26,0.3), 0 0 40px rgba(212,137,26,0.1); }
          50%       { box-shadow: 0 0 30px rgba(212,137,26,0.5), 0 0 60px rgba(212,137,26,0.2); }
        }
        .hero-tag,
        .hero-name,
        .hero-subtitle,
        .hero-quote,
        .hero-line,
        .hero-desc,
        .hero-portrait,
        .hero-btn {
          opacity: 0;
        }
        .hero-animated .hero-tag {
          animation: heroFadeUp 1s cubic-bezier(0.22,1,0.36,1) 0.1s forwards;
        }
        .hero-animated .hero-name {
          animation: heroNameReveal 1.4s cubic-bezier(0.22,1,0.36,1) 0.45s forwards;
        }
        .hero-animated .hero-subtitle {
          animation: heroSubtitleSlide 1.2s cubic-bezier(0.22,1,0.36,1) 1s forwards;
        }
        .hero-animated .hero-quote {
          animation: heroQuoteFade 1.6s cubic-bezier(0.22,1,0.36,1) 1.6s forwards;
        }
        .hero-animated .hero-line {
          animation: heroLineExpand 1.2s cubic-bezier(0.22,1,0.36,1) 2.2s forwards;
          transform-origin: center;
        }
        .hero-animated .hero-desc {
          animation: heroDescFade 1.4s cubic-bezier(0.22,1,0.36,1) 2.6s forwards;
        }
        .hero-animated .hero-portrait {
          animation: heroPortraitIn 1.6s cubic-bezier(0.22,1,0.36,1) 0.8s forwards;
        }
        .hero-animated .hero-btn {
          animation: heroButtonFade 1s cubic-bezier(0.22,1,0.36,1) 2.4s forwards;
        }
      `}</style>

      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/c381a7b048d06c420df2695d8fd6f786.jpeg"
          alt=""
          className="w-full h-full object-cover object-top brightness-[1.22] contrast-[0.82]"
          style={{ animation: 'kenBurnsLoop 24s ease-in-out infinite' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-deep-black via-deep-black/30 to-deep-black/10"></div>
      </div>

      {/* Content */}
      <div className="relative min-h-screen flex items-end md:items-end pb-10 md:pb-32 pt-24 md:pt-0 px-4 md:px-6">
        <div className="max-w-7xl mx-auto w-full py-6 md:py-0">
          {/* 桌面端：flex-row 左文右图；手机端：单列，顺序由各子元素控制 */}
          <div className="flex flex-col md:flex-row items-end justify-between gap-4 md:gap-12">

            {/* Left: Text Content */}
            <div className="max-w-2xl w-full">

              {/* Tag */}
              <div className="hero-tag inline-flex items-center gap-2 px-3 py-1.5 border border-champagne-gold/50 rounded-sm mb-3 md:mb-6">
                <i className="ri-music-2-line text-champagne-gold text-xs"></i>
                <span className="text-champagne-gold font-cormorant text-xs tracking-wider">BARITONE</span>
              </div>

              {/* Main Title */}
              <div className="mb-3 md:mb-6" style={{ textShadow: '0 2px 18px rgba(0,0,0,0.95), 0 1px 6px rgba(0,0,0,0.85)' }}>
                <h2 className="hero-name text-6xl md:text-6xl font-cormorant-sc font-bold text-champagne-gold mb-1 md:mb-2 leading-tight">
                  Wei Chen
                </h2>
                <h3 className="hero-subtitle text-base md:text-3xl font-playfair font-semibold leading-tight" style={{ color: '#ede8d8', textShadow: '0 2px 18px rgba(0,0,0,0.95), 0 1px 6px rgba(0,0,0,0.85)' }}>
                  Classical Baritone
                </h3>
              </div>

              {/* Subtitle / Italian Quote */}
              <p className="hero-quote font-cormorant italic text-lg md:text-xl font-bold md:font-normal mb-4 md:mb-8 leading-snug" style={{ color: '#7aab82', textShadow: '0 2px 24px rgba(0,0,0,1), 0 1px 14px rgba(0,0,0,1), 0 0 40px rgba(0,0,0,1), 0 4px 8px rgba(0,0,0,0.95)' }}>
                &ldquo;L&apos;opera è la gemma più scintillante sulla corona della musica classica.&rdquo;
              </p>

              {/* 手机端：肖像画 + View Gallery 插入在此处（桌面端隐藏） */}
              <div className="flex flex-col items-center gap-3 mb-5 md:hidden w-[55%] mx-auto">
                <div className="hero-portrait relative group cursor-pointer w-full" onClick={() => window.REACT_APP_NAVIGATE('/gallery')}>
                  <div
                    className="relative overflow-hidden w-full"
                    style={{
                      borderRadius: '0',
                      boxShadow: '0 6px 18px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.25)',
                    }}
                  >
                    <img
                      src="https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/63bb5315c1f8ff069741ca5233ea26a6.jpeg"
                      alt="Wei Chen Portrait"
                      className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-deep-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/gallery')}
                  className="hero-btn group flex items-center gap-1.5 px-4 py-2 border border-champagne-gold/60 rounded-sm hover:bg-champagne-gold/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <span className="font-cormorant italic text-sm" style={{ color: '#d4891a', textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>
                    View Gallery
                  </span>
                  <i className="ri-arrow-right-line text-champagne-gold text-sm transition-transform duration-300 group-hover:translate-x-1"></i>
                </button>
              </div>

              {/* Decorative Line */}
              <div className="hero-line flex items-center gap-3 mb-3 md:mb-8">
                <div className="h-px w-12 md:w-24 bg-champagne-gold"></div>
                <div className="w-4 h-4 flex items-center justify-center text-champagne-gold opacity-60">
                  <i className="ri-music-fill text-xs"></i>
                </div>
                <div className="h-px w-12 md:w-24 bg-champagne-gold"></div>
              </div>

              {/* Description */}
              <p className="hero-desc font-cormorant text-base md:text-lg leading-relaxed max-w-xl mb-8 md:mb-0" style={{ color: '#ede8d8', textShadow: '0 1px 10px rgba(0,0,0,0.95), 0 2px 20px rgba(0,0,0,0.8)' }}>
                Wei Chen is a professional Baritone specializing in Classical Voice, born in China. He completed his undergraduate studies at the Xinghai Conservatory of Music, earned a Master of Music degree from the Manhattan School of Music in New York, and received a Professional Studies Diploma with a full scholarship from the Mannes School of Music. He is currently represented by{' '}
                <a
                  href="https://www.vpartistsmanagement.com/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="underline underline-offset-2 hover:opacity-80 transition-colors"
                  style={{ color: '#c9a84c' }}
                >
                  VP Artists Management
                </a>
                {' '}and is based in New York City.
              </p>
            </div>

            {/* Right: Portrait Photo（桌面端显示，手机端隐藏） */}
            <div className="hidden md:flex flex-col items-center gap-6 md:w-[24%] lg:w-[21%] self-auto">
              <div className="hero-portrait relative group cursor-pointer w-full" onClick={() => window.REACT_APP_NAVIGATE('/gallery')}>
                <div
                  className="relative overflow-hidden w-full"
                  style={{
                    borderRadius: '0',
                    boxShadow: '0 10px 28px rgba(0,0,0,0.38), 0 4px 10px rgba(0,0,0,0.25)',
                  }}
                >
                  <img
                    src="https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/63bb5315c1f8ff069741ca5233ea26a6.jpeg"
                    alt="Wei Chen Portrait"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-deep-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>

              {/* View Gallery Button */}
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/gallery')}
                className="hero-btn group flex items-center gap-1.5 px-6 py-3 border border-champagne-gold/60 rounded-sm hover:bg-champagne-gold/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <span className="font-cormorant italic text-lg" style={{ color: '#d4891a', textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>
                  View Gallery
                </span>
                <i className="ri-arrow-right-line text-champagne-gold text-lg transition-transform duration-300 group-hover:translate-x-1"></i>
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 animate-bounce cursor-pointer hidden md:flex">
        <span className="font-cormorant text-xs tracking-widest" style={{ color: '#c9a84c', textShadow: '0 1px 8px rgba(0,0,0,0.9)' }}>SCROLL</span>
        <i className="ri-arrow-down-line text-champagne-gold/60 text-xl"></i>
      </div>
    </section>
  );
}